import React, { useState, useEffect } from 'react'
import { useAuth } from '../../hooks/useAuth'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import { api } from '../../services/api'
import {
  CalendarDaysIcon,
  ClockIcon,
  PlusIcon,
  TrashIcon,
  CheckIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CogIcon,
  PlayIcon,
  StopIcon
} from '@heroicons/react/24/outline'

interface ProviderAvailability {
  id: string
  providerId: string
  date: string
  startTime: string
  endTime: string
  isAvailable: boolean
  maxBookings: number
  currentBookings: number
  serviceType?: string
  createdAt: string
  updatedAt: string
}

interface WeeklySchedule {
  [day: string]: {
    enabled: boolean
    ranges: { start: string; end: string }[]
  }
}

interface CalendarOverride {
  [date: string]: boolean | undefined // true=available, false=unavailable, undefined=default
}

const ProviderSchedulePage: React.FC = () => {
  const { user } = useAuth()
  const [availabilities, setAvailabilities] = useState<ProviderAvailability[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [activeTab, setActiveTab] = useState<'weekly' | 'calendar'>('weekly')
  const [monthlyOverrides, setMonthlyOverrides] = useState<CalendarOverride>({})
  const [currentMonth, setCurrentMonth] = useState(new Date())

  // Weekly schedule template
  const [weeklySchedule, setWeeklySchedule] = useState<WeeklySchedule>({
    Monday: { enabled: false, ranges: [] },
    Tuesday: { enabled: false, ranges: [] },
    Wednesday: { enabled: false, ranges: [] },
    Thursday: { enabled: false, ranges: [] },
    Friday: { enabled: false, ranges: [] },
    Saturday: { enabled: false, ranges: [] },
    Sunday: { enabled: false, ranges: [] }
  })

  const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  useEffect(() => {
    const loadAllData = async () => {
      setLoading(true)
      try {
        await Promise.all([
          fetchAvailabilities(),
          fetchWeeklyTemplate(),
          fetchCalendarOverrides()
        ])
      } catch (err) {
        console.error('Failed to load schedule data:', err)
      } finally {
        setLoading(false)
      }
    }

    loadAllData()
  }, [])

  const fetchAvailabilities = async () => {
    try {
      // Not needed anymore since we use the new schedule system
      setAvailabilities([])
    } catch (err: any) {
      console.error('Failed to load availabilities:', err)
    }
  }

  const fetchWeeklyTemplate = async () => {
    try {
      const response = await api.get('/provider-dashboard/schedule/weekly')
      if (response.data && response.data.schedule) {
        setWeeklySchedule(response.data.schedule)
      }
    } catch (err: any) {
      console.error('Failed to load weekly template:', err)
    }
  }

  const fetchCalendarOverrides = async () => {
    try {
      const today = new Date()
      const endDate = new Date(today.getFullYear(), today.getMonth() + 3, today.getDate()) // 3 months from now
      const response = await api.get('/provider-dashboard/schedule/calendar', {
        params: {
          startDate: today.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        }
      })
      if (response.data && response.data.overrides) {
        setMonthlyOverrides(response.data.overrides)
      }
    } catch (err: any) {
      console.error('Failed to load calendar overrides:', err)
    }
  }

  const saveWeeklyTemplate = async () => {
    setSaving(true)
    try {
      await api.post('/provider-dashboard/schedule/weekly', weeklySchedule)

      toast.success('Weekly schedule template saved successfully!')
      fetchWeeklyTemplate()
    } catch (err: any) {
      toast.error('Failed to save weekly schedule')
      console.error('Error saving weekly schedule:', err)
    } finally {
      setSaving(false)
    }
  }

  const saveCalendarOverrides = async () => {
    setSaving(true)
    try {
      await api.post('/provider-dashboard/schedule/calendar', {
        overrides: monthlyOverrides
      })

      toast.success('Calendar overrides saved successfully!')
      fetchCalendarOverrides()
      setMonthlyOverrides({})
    } catch (err: any) {
      toast.error('Failed to save calendar overrides')
      console.error('Error saving calendar overrides:', err)
    } finally {
      setSaving(false)
    }
  }

  const toggleDay = (dayName: string) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayName]: {
        ...prev[dayName],
        enabled: !prev[dayName].enabled,
        ranges: !prev[dayName].enabled ? [] : prev[dayName].ranges.length > 0 ? prev[dayName].ranges : [{ start: '09:00', end: '17:00' }]
      }
    }))
  }

  const addTimeRange = (dayName: string) => {
    const daySchedule = weeklySchedule[dayName]
    const newRange = { start: '09:00', end: '17:00' }

    setWeeklySchedule(prev => ({
      ...prev,
      [dayName]: {
        ...prev[dayName],
        ranges: [...prev[dayName].ranges, newRange]
      }
    }))
  }

  const removeTimeRange = (dayName: string, index: number) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [dayName]: {
        ...prev[dayName],
        ranges: prev[dayName].ranges.filter((_, i) => i !== index)
      }
    }))
  }

  const applyPreset = (preset: string) => {
    const newSchedule: WeeklySchedule = {} as WeeklySchedule

    dayNames.forEach(day => {
      newSchedule[day] = { enabled: false, ranges: [] }
    })

    switch (preset) {
      case '24/7':
        dayNames.forEach(day => {
          newSchedule[day] = { enabled: true, ranges: [{ start: '00:00', end: '23:59' }] }
        })
        break
      case 'business':
        dayNames.forEach((day, idx) => {
          newSchedule[day] = {
            enabled: idx < 5,
            ranges: idx < 5 ? [{ start: '09:00', end: '17:00' }] : []
          }
        })
        break
      case 'extended':
        dayNames.forEach((day, idx) => {
          newSchedule[day] = {
            enabled: idx < 6,
            ranges: idx < 6 ? [{ start: '07:00', end: '22:00' }] : []
          }
        })
        break
      case 'weekdays':
        dayNames.forEach((day, idx) => {
          newSchedule[day] = {
            enabled: idx < 5,
            ranges: idx < 5 ? [{ start: '08:00', end: '18:00' }] : []
          }
        })
        break
      case 'clear':
        // Already initialized above
        break
    }

    setWeeklySchedule(newSchedule)
    toast.success(`Preset "${preset}" applied`)
  }

  const changeMonth = (delta: number) => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const toggleCalendarOverride = (dateStr: string) => {
    setMonthlyOverrides(prev => ({
      ...prev,
      [dateStr]: prev[dateStr] === false ? undefined : false
    }))
  }

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const minute = parseInt(minutes)
    const period = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour)
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`
  }

  const getWeekDaysInMonth = () => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()

    const weeks = []
    let currentWeek = []

    // Empty cells for first week
    for (let i = 0; i < firstDay; i++) {
      currentWeek.push(null)
    }

    // Add all days
    for (let day = 1; day <= daysInMonth; day++) {
      currentWeek.push(day)

      if (currentWeek.length === 7) {
        weeks.push(currentWeek)
        currentWeek = []
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) {
        currentWeek.push(null)
      }
      weeks.push(currentWeek)
    }

    return weeks
  }

  const getActiveDaysCount = () => {
    return Object.values(weeklySchedule).filter(day => day.enabled).length
  }

  const getTotalTimeSlots = () => {
    return Object.values(weeklySchedule).reduce((sum, day) => sum + day.ranges.length, 0)
  }

  const getOverrideDaysCount = () => {
    return Object.values(monthlyOverrides).filter(val => val === false).length
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-green-600 text-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <div className="bg-white/20 p-3 rounded-full">
            <CalendarDaysIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Availability Management</h1>
            <p className="text-green-100">Set your service hours and manage exceptions</p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-green-100 p-3 rounded-full">
              <PlayIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Active Days</h3>
              <p className="text-2xl font-bold text-green-600">
                {getActiveDaysCount()}/7
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-blue-100 p-3 rounded-full">
              <ClockIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Time Slots</h3>
              <p className="text-2xl font-bold text-blue-600">
                {getTotalTimeSlots()}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-yellow-100 p-3 rounded-full">
              <CalendarDaysIcon className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">Calendar Days</h3>
              <p className="text-2xl font-bold text-yellow-600">
                {getOverrideDaysCount()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Presets */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">⚡ Quick Presets</h3>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => applyPreset('24/7')}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:border-green-500 hover:text-green-600 transition-colors"
          >
            24/7 Service
          </button>
          <button
            onClick={() => applyPreset('business')}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:border-green-500 hover:text-green-600 transition-colors"
          >
            Business Hours
          </button>
          <button
            onClick={() => applyPreset('extended')}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:border-green-500 hover:text-green-600 transition-colors"
          >
            Extended Hours
          </button>
          <button
            onClick={() => applyPreset('weekdays')}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:border-green-500 hover:text-green-600 transition-colors"
          >
            Weekdays Only
          </button>
          <button
            onClick={() => applyPreset('clear')}
            className="px-3 py-2 bg-white border border-gray-300 rounded-md text-sm hover:border-green-500 hover:text-green-600 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <nav className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('weekly')}
            className={`flex items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'weekly'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CalendarDaysIcon className="h-4 w-4 mr-2" />
            Weekly Schedule
          </button>
          <button
            onClick={() => setActiveTab('calendar')}
            className={`flex items-center px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'calendar'
                ? 'border-green-500 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CalendarDaysIcon className="h-4 w-4 mr-2" />
            Calendar Overrides
          </button>
        </nav>

        <div className="p-6">
          {activeTab === 'weekly' ? (
            <div className="space-y-4">

              {/* Weekly Grid */}
              <div className="space-y-3">
                {dayNames.map(dayName => {
                  const dayData = weeklySchedule[dayName]
                  return (
                    <div key={dayName} className={`border rounded-lg overflow-hidden transition-all ${dayData.enabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                      <div className={`flex items-center justify-between p-4 ${dayData.enabled ? 'bg-green-100' : 'bg-gray-50'}`}>
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => toggleDay(dayName)}
                            className={`w-12 h-6 rounded-full transition-colors ${
                              dayData.enabled ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                          >
                            <div
                              className={`w-5 h-5 bg-white rounded-full transition-transform ${
                                dayData.enabled ? 'translate-x-6' : 'translate-x-0'
                              }`}
                            />
                          </button>
                          <span className="font-medium text-gray-900">{dayName}</span>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          dayData.enabled ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {dayData.enabled ? 'Active' : 'Closed'}
                        </span>
                      </div>

                      {dayData.enabled && (
                        <div className="p-4 space-y-3 bg-white">
                          <div className="flex items-center space-x-2">
                            <input
                              type="time"
                              defaultValue="09:00"
                              className="px-3 py-2 border border-gray-300 rounded-md"
                              id={`${dayName}-start`}
                            />
                            <span className="text-sm text-gray-600">to</span>
                            <input
                              type="time"
                              defaultValue="17:00"
                              className="px-3 py-2 border border-gray-300 rounded-md"
                              id={`${dayName}-end`}
                            />
                            <button
                              onClick={() => addTimeRange(dayName)}
                              className="px-3 py-1 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 transition-colors"
                            >
                              + Add
                            </button>
                          </div>

                          {dayData.ranges.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {dayData.ranges.map((range, idx) => (
                                <div
                                  key={idx}
                                  className="inline-flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm"
                                >
                                  {formatTime(range.start)} - {formatTime(range.end)}
                                  <button
                                    onClick={() => removeTimeRange(dayName, idx)}
                                    className="text-green-600 hover:text-green-800 font-bold ml-1"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Calendar Month Navigation */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <button
                  onClick={() => changeMonth(-1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                <h3 className="text-lg font-semibold text-gray-900">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                <button
                  onClick={() => changeMonth(1)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 p-2">
                    {day}
                  </div>
                ))}
                {getWeekDaysInMonth().map((week, weekIndex) => (
                  <React.Fragment key={weekIndex}>
                    {week.map((day, dayIndex) => {
                      if (day === null) {
                        return <div key={`${weekIndex}-${dayIndex}`} className="p-2" />
                      }

                      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
                      const dateStr = date.toISOString().split('T')[0]
                      const isPast = date < new Date()
                      const isOverride = monthlyOverrides[dateStr] === false
                      const isAvailable = monthlyOverrides[dateStr] === true || (!isOverride && !isPast)

                      return (
                        <div
                          key={dateStr}
                          className={`aspect-square border-2 rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all p-1 ${
                            isPast
                              ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                              : isOverride
                                ? 'border-red-300 bg-red-100 text-red-700 hover:bg-red-200'
                                : isAvailable
                                ? 'border-green-300 bg-green-100 text-green-700 hover:bg-green-200'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                          onClick={() => !isPast && toggleCalendarOverride(dateStr)}
                        >
                          <div className="text-sm font-medium">{day}</div>
                          <div className="text-xs mt-1">
                            {isPast ? 'Past' : isOverride ? 'Unavailable' : isAvailable ? 'Available' : 'Closed'}
                          </div>
                        </div>
                      )
                    })}
                  </React.Fragment>
                ))}
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  💡 Tip: Click dates to mark as unavailable (holidays, maintenance, etc). These override your weekly schedule.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Save Section */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4 flex justify-end space-x-3">
          <button
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            onClick={() => {
              const active = getActiveDaysCount()
              const totalRanges = getTotalTimeSlots()
              const exceptions = getOverrideDaysCount()
              alert(`Schedule Preview:\n\n✅ Active Days: ${active}/7\n⏰ Total Time Slots: ${totalRanges}\n🚫 Exception Days: ${exceptions}`)
            }}
          >
            Preview
          </button>
          <button
            onClick={() => activeTab === 'weekly' ? saveWeeklyTemplate() : saveCalendarOverrides()}
            disabled={saving}
            className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:bg-green-400 transition-colors flex items-center space-x-2"
          >
            {saving && <LoadingSpinner size="sm" />}
            {activeTab === 'weekly' ? 'Save Availability' : 'Save Calendar Overrides'}
          </button>
        </div>
      </div>

      {/* Recent Availability */}
      {availabilities.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Availability</h2>
          <div className="space-y-2">
            {availabilities.slice(0, 5).map((availability) => (
              <div key={availability.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">
                    {new Date(availability.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatTime(availability.startTime)} - {formatTime(availability.endTime)}
                  </div>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  availability.isAvailable
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {availability.isAvailable ? 'Available' : 'Unavailable'}
                </span>
              </div>
            ))}
            {availabilities.length > 5 && (
              <div className="text-center text-gray-500 text-sm">
                And {availabilities.length - 5} more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProviderSchedulePage